import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowLeft, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Icon } from '../../../../../components/Icon';
import { AllowanceCard, MilestonesCard, ProjectProgressCard } from '../../../../../components/projects/client-panels';
import { TaskList } from '../../../../../components/tasks/task-list';
import { EmptyState } from '../../../../../components/ui/empty-state';
import { useToast } from '../../../../../components/ui/toast';
import { formatDay } from '../../../../../lib/project-display';
import { projectQuery } from '../../../../../lib/projects-api';
import { taskListQuery } from '../../../../../lib/tasks-api';
import { PageSkeleton } from '../../../../../components/ui/skeleton';
import { LoadFailure } from '../../../../../components/ui/state-views';
import { toasts } from '../../../../../lib/toast-presets';

export const Route = createFileRoute('/subdomain/app/_client/projects/$projectId')({
  component: ProjectPage,
});

const PAGE =
  'flex flex-col gap-(--mbm-space-6) px-(--mbm-space-4) py-(--mbm-space-6) lg:px-(--mbm-space-9) lg:py-(--mbm-space-7)';

function BackLink() {
  return (
    <Link
      to="/subdomain/app/projects"
      search={{ q: undefined }}
      className="inline-flex items-center gap-(--mbm-space-2) self-start text-label-medium text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
    >
      <Icon icon={ArrowLeft} size="sm" />
      Projects
    </Link>
  );
}

/** The project's tasks in the client's own wording: what needs them first, finished work folded away. */
function ProjectTasks({ projectId }: { projectId: string }) {
  const [showDone, setShowDone] = useState(false);
  const open = useInfiniteQuery(taskListQuery({ projectId, tab: 'open' }));
  const done = useInfiniteQuery(taskListQuery({ projectId, tab: 'done' }));
  const doneCount = (done.data?.pages.flatMap((p) => p.items).length ?? 0) + (done.hasNextPage ? 1 : 0);

  return (
    <section className="flex flex-col gap-(--mbm-space-1) rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-3) shadow-(--mbm-shadow-table-card)">
      <TaskList
        className="flex flex-col gap-(--mbm-space-1)"
        query={open}
        audience="client"
        emptyState={
          <EmptyState
            title="No tasks to show yet"
            description="Posts, reels and updates for this project will appear here as your team gets to them."
          />
        }
      />
      {doneCount > 0 ? (
        <>
          <button
            type="button"
            aria-expanded={showDone}
            onClick={() => setShowDone((v) => !v)}
            className="flex items-center gap-(--mbm-space-2) pb-(--mbm-space-2) pl-(--mbm-space-3) pt-(--mbm-space-4) text-left text-label-large text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            <Icon icon={showDone ? CaretDown : CaretRight} size="sm" />
            Done <span className="text-text-tertiary">{done.hasNextPage ? `${doneCount - 1}+` : doneCount}</span>
          </button>
          {showDone ? (
            <TaskList className="flex flex-col gap-(--mbm-space-1)" query={done} audience="client" emptyState={null} />
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/** Client project page (Figma 296:8777). The stored rollup is the headline number. */
function ProjectPage() {
  const { projectId } = Route.useParams();
  const toast = useToast();
  const { data: project, isLoading, error, refetch } = useQuery(projectQuery(projectId));

  if (isLoading) {
    return (
      <main className={PAGE}>
        <BackLink />
        <PageSkeleton label="Loading the project" />
      </main>
    );
  }

  if (!project) {
    return (
      <main className={PAGE}>
        <BackLink />
        <LoadFailure
          error={error}
          noun="project"
          forbiddenHint="It isn’t part of your business."
          notFoundHint="It may have been removed, or it isn’t part of your business."
          className="rounded-(--mbm-component-radius-widget) bg-surface-card shadow-(--mbm-shadow-table-card)"
          onRetry={() => void refetch()}
        />
      </main>
    );
  }

  const dates = [formatDay(project.startDate), project.targetEndDate ? formatDay(project.targetEndDate) : null]
    .filter(Boolean)
    .join(' – ');
  const manager = project.accountManagerName;

  return (
    <main className={PAGE}>
      <div className="flex flex-wrap items-end justify-between gap-(--mbm-space-4)">
        <header className="flex flex-col gap-(--mbm-space-1)">
          <BackLink />
          <h1 className="text-heading-page text-text-strong">{project.name}</h1>
          <p className="text-body-compact text-text-secondary">
            {[project.description, dates, manager ? `${manager}, account manager` : null].filter(Boolean).join(' · ')}
          </p>
        </header>
        {manager ? (
          <button
            type="button"
            onClick={() => toast(toasts.comingSoon('Messaging your team arrives with chat.'))}
            className="inline-flex h-(--mbm-size-button-compact) items-center justify-center rounded-(--mbm-component-radius-button) bg-action-secondary px-(--mbm-space-6) text-label-large text-text-primary shadow-(--mbm-shadow-sm) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            Message {manager.split(' ')[0]}
          </button>
        ) : null}
      </div>

      <div className="grid items-start gap-(--mbm-space-4) lg:grid-cols-[minmax(0,1fr)_minmax(0,24.8rem)]">
        <ProjectTasks projectId={project.id} />
        <aside className="flex flex-col gap-(--mbm-space-4)">
          <ProjectProgressCard project={project} />
          <AllowanceCard />
          <MilestonesCard project={project} />
        </aside>
      </div>
    </main>
  );
}
